import React, { useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import GreenSubmitButton from '../buttons/greenSubmitButton';
import AppColors from '../assets/colors';
import { inject, observer } from "mobx-react";
import VerificationErrorModal from './verificationErrorModal';
import { DEFAULT_EMAIL, DEFAULT_PSW } from "../assets/globalConstants";
import SmallAlert from "./smallAlert";
import { withTimeout } from "../utils/withTimeout";
import { restoreGuestSession, startSignIn } from "../utils/guestSession";
import Box from '@mui/material/Box';

// Translations
import _useTranslate from '../hooks/_useTranslate';

const style = {
  position: 'absolute',
  width: {
    xs: '100%',
    sm: '80%',
    md: 600,
  },
  height: 600,
  top: {xs: 60, sm: '50%'},
  left: {xs: 0, sm: '50%'},
  transform: {sm: 'translate(-50%, -50%)'},
  bgcolor: 'background.paper',
  border: '2px solid var(--border-color)',
  borderRadius: 4,
  boxShadow: 24,
  p: 4,
};

//The server says how long its codes are (applications.create answers codeDigits); 4 until it says otherwise.
const DEFAULT_CODE_DIGITS = 4;

/* The sign-up code is checked by the SERVER (feathersStore.verifyApplication -> verifications service),
   which also makes the account; this screen only collects the code and signs in afterwards with the
   email/password the customer registered with. It used to read the code back from emaillog as the shared
   guest and compare it here, which proved nothing - the account could be made without it.

   registrationCard logs out after creating the application, so while this is open nobody is signed in.
   Leaving it any way but success puts the shared guest back, or the storefront is left signed out. */
const VerificationCode = ({visible, onClickCloseCode, payload,  feathersStore})=> {

  let common = _useTranslate(feathersStore.language, "common");

  const digits = Number.isInteger(payload?.codeDigits) && payload.codeDigits > 0 ? payload.codeDigits : DEFAULT_CODE_DIGITS;
  const [values, setValues] = useState(() => Array(digits).fill(''));
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState(null);   // {message, retry, resend} - the error modal
  const [notice, setNotice] = useState(null);     // {mode, message} - the toast
  const boxes = useRef([]);
  const completed = useRef(false);
  const busy = useRef(false);     //a verify or a resend is on its way to the server

  //A fresh screen for every application shown.
  useEffect(() => {
    if (!visible) return;
    completed.current = false;
    setValues(Array(digits).fill(''));
    setProblem(null);
  }, [visible, payload?.applicationId, digits]);

  const clear = () => {
    setValues(Array(digits).fill(''));
    boxes.current[0]?.focus();
  };

  //Digits from `index` on, one per box; focus lands after the last one written.
  const fillFrom = (index, chars) => {
    setValues(current => current.map((v, i) => (i >= index && i - index < chars.length ? chars[i - index] : v)));
    boxes.current[Math.min(index + chars.length, digits - 1)]?.focus();
  };

  /* One digit per box, but not one digit per event: the phone's one-time-code autofill (autoComplete below)
     and keyboard suggestions put the whole code into the focused box at once, which a maxLength of 1 used to
     cut to its first digit. Typing over a filled box keeps the new digit. */
  const handleChange = index => e => {
    let typed = (e.target.value || '').replace(/\D/g, '');
    if (values[index] && typed.length === 2) typed = typed.replace(values[index], '');
    if (typed.length === 0) {
      setValues(current => current.map((v, i) => (i === index ? '' : v)));
      return;
    }
    fillFrom(index, typed.slice(0, digits - index));
  };

  const handleKeyDown = index => e => {
    if (e.key === 'Backspace' && !values[index] && index > 0) boxes.current[index - 1]?.focus();
  };

  //The code arrives by email: pasting it whole fills every box, wherever it is pasted.
  const handlePaste = index => e => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    if (pasted.length >= digits) fillFrom(0, pasted.slice(0, digits));
    else fillFrom(index, pasted.slice(0, digits - index));
  };

  //Bounded, and never leaves a sign-in that cannot finish in charge of the session (utils/guestSession.js says
  //how). `abandoned`: the customer sign-in given up on below (startSignIn), which may still be to go out.
  const restoreGuest = (abandoned) =>
    restoreGuestSession(feathersStore, { email: DEFAULT_EMAIL, password: DEFAULT_PSW, abandoned });

  //What the server's refusal means for the customer (error.data.reason, feathers-server
  //applications/verification.js). null: not a refusal of the code - shown as it came.
  const problemFor = error => {
    switch (error?.data?.reason) {
      case 'invalid':
        //The guess that used up the code's last attempt: another guess would only be refused - a new code it is.
        if (error.data.attemptsLeft === 0) {
          return { message: common.codeExpired || "This code has expired or has been tried too many times. Send a new one.", resend: true };
        }
        return { message: common.errorCode, retry: true, resend: true };
      case 'expired':
      case 'attempts':
      case 'no-code':
        return { message: common.codeExpired || "This code has expired or has been tried too many times. Send a new one.", resend: true };
      case 'too-soon':
        return { message: common.codeTooSoon || "A code was just sent. Please wait a minute before asking for another.", retry: true };
      //utils/withTimeout.js: no answer - the customer stays and tries again.
      case 'timeout':
        return { message: common.codeNoAnswer || "No answer from the shop. Check your connection and try again.", retry: true };
      //The application's last code was sent - and still works: the customer stays and types it.
      case 'send-limit':
        return { message: common.codeSendLimit || "No more codes can be sent for this registration. Enter the last code you received, or register again.", retry: true };
      case 'locked':
        return { message: common.codeLocked || "Too many attempts for this address. Please try again later." };
      //Older than the server keeps applications, or removed in the back office: only a new sign-up helps.
      case 'stale':
      case 'unknown':
        return { message: common.codeRegisterAgain || "This registration is no longer valid. Please register again." };
      case 'email-taken':
        return { message: common.codeEmailTaken || "An account with this email already exists. Sign in, or reset your password." };
      case 'processed':
        return { message: common.codeAlreadyDone || "Your account is ready. Please sign in." };
      default:
        return null;
    }
  };

  const showFailure = error => {
    const known = problemFor(error);
    if (known) setProblem(known);
    else setNotice({ mode: 'error', message: error?.message || String(error) });
  };

  /* Leaving without an account: the error modal's exit, or closing this one. Not while a verify or a resend
     is out: signing the guest in then, with the verify about to make and sign in the account, raced the two. */
  const leave = async () => {
    if (busy.current) return;
    setProblem(null);
    onClickCloseCode();
    if (!completed.current) await restoreGuest();
  };

  const resend = async () => {
    if (busy.current) return;
    busy.current = true;
    setProblem(null);
    try {
      await feathersStore.resendVerificationCode(payload?.applicationId);
      busy.current = false;
      clear();
      setNotice({ mode: 'success', message: common.codeResent || "A new code has been sent to your email." });
    } catch (error) {
      busy.current = false;
      showFailure(error);
    }
  };

  const submit = async () => {
    if (busy.current) return;
    busy.current = true;
    const pin = values.join('');
    const { applicationId, email, password } = payload || {};
    setLoading(true);
    try {
      await feathersStore.verifyApplication(applicationId, pin);
    } catch (error) {
      busy.current = false;
      setLoading(false);
      clear();
      showFailure(error);
      return;
    }
    //The account exists now, whatever happens next.
    completed.current = true;
    /* The sign-in is bounded like the verify before it: a socket that drops in this one round trip never
       answers, and the busy screen spun on with the account already made. Refused or unanswered, the guest goes
       back and the customer is told to sign in. */
    let signedIn = false;
    const { attempt, abandoned } = startSignIn(feathersStore, email, password);
    try {
      await withTimeout(attempt);
      signedIn = true;
    } catch (error) {
      console.log('error while authenticating: ', error);
      await restoreGuest(error?.data?.reason === 'timeout' ? abandoned : undefined);
      setNotice({ mode: 'success', message: common.codeAlreadyDone || "Your account is ready. Please sign in." });
    }
    //Signed in: the session (user, categories, settings) loads now - or, if this goes unanswered, on the
    //reconnect (feathersStore's 'connect' handler runs setObservables again). The customer is in either way.
    if (signedIn) {
      try {
        await withTimeout(feathersStore.setObservables());
      } catch (error) {
        console.log('error while loading the session: ', error);
      }
    }
    busy.current = false;
    setLoading(false);
    onClickCloseCode();
  };

  const info = (common.verificationInfo || '').replace('4', `${digits}`);

  return (
    <section>
      <Modal
        open={visible}
        onClose={leave}
        aria-labelledby="login-modal"
        aria-describedby="login-or-register"
      >
      <Box sx={style}>
        <div className="modalContainer">
          <div className="modalTitleContainer">
            <h1>{common.verificationCode}</h1>
          </div>
          <p style={{color: 'var(--secondaryText)'}}>{info}<br/> {payload?.email}</p>
          <div className="modalVerificationCodeContainer">
            {values.map((value, index) => (
              <input className="modalVerificationCodeBox"
                key={index}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                value={value}
                onChange={handleChange(index)}
                onKeyDown={handleKeyDown(index)}
                onPaste={handlePaste(index)}
                ref={(element) => { boxes.current[index] = element; }}
                required
                aria-label='verification code'
                style={{backgroundColor: value.length > 0 && AppColors.amazonColor}}
              />
            ))}
          </div>
          <div className="modalFacebookContainer">
            <GreenSubmitButton
              aria-labelledby='Submit'
              title={common.enterCode}
              loading={loading}
              onClick={submit}
              disabled={loading || values.some(value => value.length < 1)}
            >
            </GreenSubmitButton>
          </div>
        </div>
        <VerificationErrorModal
          visible={!!problem}
          message={problem?.message}
          retry={problem?.retry ? () => setProblem(null) : undefined}
          resend={problem?.resend ? resend : undefined}
          exit={leave}
        >
        </VerificationErrorModal>
        </Box>
      </Modal>
      <SmallAlert
        show={!!notice}
        mode={notice?.mode === 'error' ? 'stock' : undefined}
        heading={common.verificationCode}
        onClose={()=>setNotice(null)}
        message={notice?.message}
     />
    </section>

  );
}

export default inject('feathersStore')(observer(VerificationCode));
